import { useFrameworksListQuery } from '@/api/queries/company/useFrameworksListQuery';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useLocaleStore } from '@/stores/locales';
import type { NavItemType } from '@/router/types';
import { computed, inject } from 'vue';
import type { GetAppsUrls } from '@/plugins/env-urls/types';

/**
 * Handles the external and non available routes on nav items
 */
export function useNavigation(navItems: NavItemType[]) {
  const { permissions, me } = useMeQuery();
  const { cFAvailable: canUseCompetencyFramework } = useFrameworksListQuery();

  const navItemsNames = navItems.map((item) => item.name);

  const appUrl = inject('appUrl') as GetAppsUrls;

  const canUseSourcing = computed(() => permissions.value && permissions.value.includes('sc_pa'));

  const isRecruiter = computed(() => permissions.value && permissions.value.includes('rct_r'));
  const isAdmin = computed(() => permissions.value && permissions.value.includes('cadm_r'));

  // Display manager link only if user is not recruiter.
  // If user is recruiter: user will access manager features through recruiter account
  const hasManagerAccess = computed(() => {
    const isManager = permissions.value && permissions.value.includes('mng_r');
    return !isRecruiter.value && isManager;
  });

  const managerOneCompany = computed(
    () => hasManagerAccess.value && !isAdmin.value && me.value?.managers?.length === 1,
  );
  const recruiterOneSharepool = computed(
    () => isRecruiter.value && me.value?.sharepools?.length === 1 && !isAdmin.value,
  );
  const adminOnly = computed(() => isAdmin.value && !hasManagerAccess.value && !isRecruiter.value);

  const recruiterRoute = computed(() => {
    const recruiterIndex = navItems.findIndex((item) => item.name === 'recruiter');
    return recruiterIndex !== -1 && recruiterOneSharepool.value
      ? `${appUrl('recruiter_account')}${navItems[recruiterIndex].route}`
      : undefined;
  });

  const adminRoute = computed(() => {
    const adminIndex = navItems.findIndex((item) => item.name === 'admin');
    return adminIndex !== -1 && adminOnly.value
      ? `${appUrl('admin_account')}${navItems[adminIndex].route}`
      : undefined;
  });

  const managerRoute = computed(() => {
    const managerIndex = navItems.findIndex((item) => item.name === 'manager');
    return managerIndex !== -1 && managerOneCompany.value
      ? `${appUrl('recruiter_account')}${navItems[managerIndex].route}`
      : undefined;
  });

  const myCompanyRoute = computed(() => {
    const companyIndex = navItems.findIndex((item) => item.name === 'my-company');

    return navItemsNames.includes('my-company') && canUseCompetencyFramework.value
      ? navItems[companyIndex].route
      : undefined;
  });

  const sourcingRoute = computed(() => {
    const sourcingIndex = navItems.findIndex((item) => item.name === 'jobs');
    return sourcingIndex !== -1 && canUseSourcing.value ? navItems[sourcingIndex].route : undefined;
  });

  const helpRoute = computed(() => {
    const helpIndex = navItems.findIndex((item) => item.name === 'help');
    const lang = useLocaleStore().currentLang === 'fr' ? 'fr' : 'en';
    return helpIndex !== -1 ? `${appUrl('help')}/${lang}${navItems[helpIndex].route}` : undefined;
  });

  const mappedItems = computed(() => {
    return navItems.map((item) => {
      if (item.name === 'recruiter') return { ...item, route: recruiterRoute.value };
      if (item.name === 'admin') return { ...item, route: adminRoute.value };
      if (item.name === 'manager') return { ...item, route: managerRoute.value };
      if (item.name === 'jobs') return { ...item, route: sourcingRoute.value };
      if (item.name === 'my-company') return { ...item, route: myCompanyRoute.value };
      if (item.name === 'help') return { ...item, route: helpRoute.value };
      return item;
    });
  });

  // Removes unavailables items (no route)
  const availableItems = computed(() => {
    return mappedItems.value.filter((item) => !!item.route);
  });

  return { availableItems };
}
